/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { Entry } from "../../redux/entrySlice";
import Loader from "../../components/Loader/Loader";
import styles from "./walletBalanceModal.module.scss";
import "react-toastify/dist/ReactToastify.css";

const WalletBalanceModal = React.memo(({ userId, userName, handleClose, setIsUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [deductAmount, setDeductAmount] = useState("");
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const actionData = {
      actionUrl: `user/${userId}/wallet`,
      actionMethod: "get",
    };
    dispatch(Entry(actionData)).then((resp) => {
      setLoading(false);
      if (resp.payload?.code === 200 && resp.payload?.data?.wallet) {
        setCurrentBalance(resp.payload.data.wallet.amount ?? 0);
      } else {
        setError("Failed to fetch wallet balance");
        setCurrentBalance(0);
      }
    });
  }, [userId, dispatch]);

  const handleSave = (e) => {
    e.preventDefault();
    const amount = parseFloat(deductAmount);

    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount greater than 0");
      return;
    }

    if (amount > currentBalance) {
      toast.error(`Amount ($${amount}) exceeds current balance ($${currentBalance.toFixed(2)})`);
      return;
    }

    setSubmitting(true);
    setError(null);
    const actionData = {
      actionUrl: `user/${userId}/wallet`,
      actionMethod: "patch",
      apiData: { deductAmount: amount },
    };
    dispatch(Entry(actionData)).then((resp) => {
      setSubmitting(false);
      if (resp.payload?.code === 200) {
        const newBalance = resp.payload.data?.wallet?.amount ?? currentBalance - amount;
        setCurrentBalance(newBalance);
        setDeductAmount("");
        toast.success(`$${amount.toFixed(2)} deducted successfully`);
        setIsUpdated?.(true);
      } else {
        setError(resp.payload?.message ?? "Failed to update wallet balance");
        toast.error(resp.payload?.message ?? "Failed to update wallet balance");
      }
    });
  };

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popupContent}>
        <div className={styles.popupHeader}>
          <h3>Wallet Balance — {userName || "User"}</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close wallet modal"
            tabIndex={0}
          >
            ×
          </button>
        </div>

        <div className={styles.popupBody}>
          {loading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : (
            <form id="wallet-form" onSubmit={handleSave}>
              <div className={styles.currentBalanceCard}>
                <span className={styles.balanceLabel}>Current Balance</span>
                <span className={styles.balanceValue}>${currentBalance.toFixed(2)}</span>
              </div>

              <div className={styles.walletBalanceCard}>
                <label htmlFor="deduct-amount" className={styles.walletLabel}>
                  Amount to Deduct ($)
                </label>
                <input
                  id="deduct-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={currentBalance}
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(e.target.value)}
                  className={styles.walletInput}
                  placeholder="0.00"
                  aria-label="Amount to deduct from wallet"
                />
                {deductAmount && !Number.isNaN(parseFloat(deductAmount)) && parseFloat(deductAmount) <= currentBalance && (
                  <p className={styles.previewMsg}>
                    New balance after deduction:{" "}
                    <strong>${(currentBalance - parseFloat(deductAmount)).toFixed(2)}</strong>
                  </p>
                )}
              </div>
              {error && <p className={styles.errorMsg}>{error}</p>}
            </form>
          )}
        </div>

        <div className={styles.popupFooter}>
          {loading ? (
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleClose}
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="submit"
                form="wallet-form"
                className={styles.saveBtn}
                disabled={submitting || !deductAmount}
              >
                {submitting ? "Deducting..." : "Deduct"}
              </button>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={handleClose}
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
      <ToastContainer autoClose={2000} />
    </div>
  );
});

WalletBalanceModal.displayName = "WalletBalanceModal";

export default WalletBalanceModal;
